import {
    Box,
    Button,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Text,
    VStack,
    HStack,
    Select,
    Icon,
    Divider,
} from '@chakra-ui/react';
import { useState, useEffect, useRef, useCallback, type ChangeEvent, type ElementType } from 'react';
import { useGetLocations } from '../hooks/useGetLocations';
import {
    CheckboxMenuSearch,
    CheckboxMenuRow,
    CheckboxMenuList,
    CheckboxMenuFooter,
    CheckboxMenuTrigger,
    CheckboxMenuPinnedAll,
    MenuRow,
    SelectionSummaryTooltip,
    TokenChip,
    checkboxMenuPopoverContentSx,
    checkboxMenuColors,
    checkboxMenuTriggerMetrics,
} from '../ui/primitives/CheckboxMenu';

interface LocationSelectorProps {
    storageKey?: string;
    selectedLocations?: Array<string> | 'all';
    onLocationChange?: (locationIds: Array<string> | 'all') => void;
    onSingleLocationChange?: (locationId: string) => void;
    singleSelect?: boolean;
    selectedLocationId?: string;
    width?: string;
    hideOtherEmployeeLocations?: boolean;
    allowEmptySelection?: boolean;
    icon?: ElementType | any;
    iconOnly?: boolean;
}

const LocationSelector = ({
    storageKey,
    selectedLocations,
    onLocationChange,
    onSingleLocationChange,
    singleSelect = false,
    selectedLocationId,
    width = "200px",
    hideOtherEmployeeLocations = false,
    allowEmptySelection = false,
    icon,
    iconOnly = false
}: LocationSelectorProps) => {
    const { data: locationsData } = useGetLocations();
    const [locationIds, setLocationIds] = useState<Array<string> | 'all'>(() => {
        if (storageKey) {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                try {
                    return stored === 'all' ? 'all' : JSON.parse(stored);
                } catch { }
            }
        }

        if (locationsData?.myLocations && locationsData.myLocations.length > 0) return locationsData.myLocations;
        if (locationsData?.locations.some(l => l._id === 'all')) return 'all';
        else if (locationsData?.locations.length) return [locationsData.locations[0]._id];
        return [];
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const [popoverWidth, setPopoverWidth] = useState<number | string>(width);
    const initializedRef = useRef(false);
    const employeeStorageKey = storageKey ? `${storageKey}-employee` : null;
    const [selectedEmployee, setSelectedEmployee] = useState<string>(() => {
        if (employeeStorageKey) {
            const stored = localStorage.getItem(employeeStorageKey);
            if (stored) return stored;
        }
        return 'none';
    });

    const getMyLocations = useCallback((): string[] => {
        if (!locationsData) return [];
        if (locationsData.myLocations === 'all') {
            return locationsData.locations
                .filter((location) => location._id !== 'all')
                .map((location) => location._id);
        }
        return Array.isArray(locationsData.myLocations) ? locationsData.myLocations : [];
    }, [locationsData]);

    useEffect(() => {
        if (selectedLocations !== undefined) {
            setLocationIds(selectedLocations);
            return;
        }

        if (locationsData && !initializedRef.current) {
            if (storageKey) {
                const stored = localStorage.getItem(storageKey);
                if (stored) {
                    try {
                        const parsed = stored === 'all' ? 'all' : JSON.parse(stored);
                        if (
                            parsed === 'all' ||
                            (Array.isArray(parsed) && parsed.length > 0)
                        ) {
                            setLocationIds(parsed);
                            onLocationChange?.(parsed);
                            initializedRef.current = true;
                            return;
                        }
                    } catch { }
                }
            }

            let initial: Array<string> | 'all' = [];

            const myLocations = getMyLocations();
            if (locationsData.myLocations === 'all') {
                initial = 'all';
            } else if (myLocations.length > 0) {
                initial = myLocations;
            } else if (locationsData.locations.some(l => l._id === 'all')) {
                initial = 'all';
            } else if (locationsData.locations.length > 0) {
                initial = [locationsData.locations[0]._id];
            }

            if (initial === 'all' || initial.length > 0) {
                setLocationIds(initial);
                onLocationChange?.(initial);
                initializedRef.current = true;
            }
        }
    }, [locationsData, selectedLocations, onLocationChange, storageKey, getMyLocations]);

    useEffect(() => {
        if (isPopoverOpen && triggerRef.current) {
            setPopoverWidth(Math.max(triggerRef.current.offsetWidth, 260));
        }
    }, [isPopoverOpen]);

    useEffect(() => {
        if (!isPopoverOpen) {
            setSearchTerm('');
        }
    }, [isPopoverOpen]);

    const handleAllLocationsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            const newValue = 'all';
            setLocationIds(newValue);
            onLocationChange?.(newValue);
            if (storageKey) {
                localStorage.setItem(storageKey, 'all');
            }
        }
    };

    const handleIndividualLocationsChange = (selectedValues: (string | number)[]) => {
        const newValue = selectedValues as string[];
        const finalValue = newValue.length === 0
            ? (allowEmptySelection ? [] : 'all')
            : newValue;
        setLocationIds(finalValue);
        onLocationChange?.(finalValue);
        if (storageKey) {
            localStorage.setItem(storageKey, finalValue === 'all' ? 'all' : JSON.stringify(finalValue));
        }
    };

    const handleMyLocationsChanges = (e: React.ChangeEvent<HTMLInputElement>) => {
        const myLocations = getMyLocations();
        if (myLocations.length === 0) return;

        if (e.target.checked) {
            // Add all myLocations to current selection
            const currentLocations = Array.isArray(locationIds) ? locationIds : [];
            const newLocations = Array.from(new Set([...currentLocations, ...myLocations]));
            setLocationIds(newLocations);
            onLocationChange?.(newLocations);
            if (storageKey) {
                localStorage.setItem(storageKey, JSON.stringify(newLocations));
            }
        } else {
            // Remove myLocations from selection (keep employee locations if any)
            const employeeLocations = getSelectedEmployeeLocations();
            const currentLocations = Array.isArray(locationIds) ? locationIds : [];
            const newLocations = currentLocations.filter(id =>
                !myLocations.includes(id) || employeeLocations.includes(id)
            );
            const finalValue = newLocations.length > 0
                ? newLocations
                : (allowEmptySelection ? [] : 'all');
            setLocationIds(finalValue);
            onLocationChange?.(finalValue);
            if (storageKey) {
                localStorage.setItem(storageKey, finalValue === 'all' ? 'all' : JSON.stringify(finalValue));
            }
        }
    };

    const handleEmployeeLocationsChange = (employeeId: string) => {
        // Get the previously selected employee's locations before changing
        const previousEmployeeLocations = getSelectedEmployeeLocations();

        setSelectedEmployee(employeeId);

        // Persist selected employee
        if (employeeStorageKey) {
            if (employeeId === 'none') {
                localStorage.removeItem(employeeStorageKey);
            } else {
                localStorage.setItem(employeeStorageKey, employeeId);
            }
        }

        if (employeeId === 'none') {
            // Remove the previous employee's locations (keep myLocations)
            if (previousEmployeeLocations.length > 0 && Array.isArray(locationIds)) {
                const myLocs = getMyLocations();
                const newLocations = locationIds.filter(id =>
                    !previousEmployeeLocations.includes(id) || myLocs.includes(id)
                );
                const finalValue = newLocations.length > 0
                    ? newLocations
                    : (allowEmptySelection ? [] : 'all');
                setLocationIds(finalValue);
                onLocationChange?.(finalValue);
                if (storageKey) {
                    localStorage.setItem(storageKey, finalValue === 'all' ? 'all' : JSON.stringify(finalValue));
                }
            }
            return;
        }

        // Add new employee's locations to current selection
        const employee = locationsData?.centralPartsEmployees?.find(e => e._id === employeeId);
        if (employee && employee.myLocations.length > 0) {
            if (locationIds === 'all') return;

            // First remove previous employee's locations (that aren't in myLocations)
            const myLocs = getMyLocations();
            let currentLocations = Array.isArray(locationIds) ? locationIds : [];
            if (previousEmployeeLocations.length > 0) {
                currentLocations = currentLocations.filter(id =>
                    !previousEmployeeLocations.includes(id) || myLocs.includes(id)
                );
            }

            // Then add new employee's locations
            const newLocations = Array.from(new Set([...currentLocations, ...employee.myLocations]));

            setLocationIds(newLocations);
            onLocationChange?.(newLocations);
            if (storageKey) {
                localStorage.setItem(storageKey, JSON.stringify(newLocations));
            }
        }
    };

    const getSelectedEmployeeLocations = () => {
        if (selectedEmployee === 'none') return [];
        const employee = locationsData?.centralPartsEmployees?.find(e => e._id === selectedEmployee);
        return employee?.myLocations ?? [];
    };

    const handleRemoveLocation = (locationIdToRemove: string) => {
        if (locationIds === 'all' || !Array.isArray(locationIds)) return;

        const newValue = locationIds.filter(id => id !== locationIdToRemove);
        const finalValue = newValue.length === 0
            ? (allowEmptySelection ? [] : 'all')
            : newValue;
        setLocationIds(finalValue);
        onLocationChange?.(finalValue);
        if (storageKey) {
            localStorage.setItem(storageKey, finalValue === 'all' ? 'all' : JSON.stringify(finalValue));
        }
    };

    const handleSingleLocationSelect = (locId: string) => {
        onSingleLocationChange?.(locId);
        setIsPopoverOpen(false);
    };

    const getButtonText = () => {
        if (singleSelect) {
            if (!selectedLocationId) return 'Select location';
            const loc = locationsData?.locations?.find(l => l._id === selectedLocationId);
            return loc?.title ?? 'Select location';
        }

        if (locationIds === 'all') {
            return 'All Locations';
        }

        const selectedLocationNames = locationIds
            .map(id => locationsData?.locations?.find(l => l._id === id)?.title)
            .filter(Boolean) as string[];

        if (selectedLocationNames.length === 0) return 'Select Locations';
        if (selectedLocationNames.length === 1) return selectedLocationNames[0];
        if (selectedLocationNames.length === 2) return selectedLocationNames.join(', ');

        const firstLocation = selectedLocationNames[0];
        const remainingCount = selectedLocationNames.length - 1;
        return `${firstLocation} +${remainingCount} more`;
    };

    const buttonText = getButtonText();

    if (
        locationsData?.locations &&
        locationsData.locations.length === 1 &&
        !locationsData.locations.some(l => l._id === 'all')
    ) {
        return null;
    }

    const locationOptions = (locationsData?.locations ?? []).filter(l => l._id !== 'all');
    const visibleLocations = locationOptions.filter(l =>
        searchTerm === '' || l.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const tokenLabels = (locationIds === 'all' || !Array.isArray(locationIds))
        ? []
        : locationIds
            .map(id => locationsData?.locations?.find(l => l._id === id)?.title)
            .filter(Boolean) as string[];
    const isSubsetSelected = !singleSelect && tokenLabels.length > 1;
    const triggerTokens = singleSelect
        ? (selectedLocationId ? [buttonText] : [])
        : isSubsetSelected
            ? [`${tokenLabels.length} selected`]
            : tokenLabels;
    const totalLocations = locationOptions.length;
    const selectedCount = locationIds === 'all'
        ? totalLocations
        : (Array.isArray(locationIds) ? locationIds.length : 0);

    return (
        <Popover
            placement="bottom-end"
            isOpen={isPopoverOpen}
            onOpen={() => setIsPopoverOpen(true)}
            onClose={() => setIsPopoverOpen(false)}
            gutter={8}
        >
            <PopoverTrigger>
                {iconOnly ? (
                    <Box ref={triggerRef}>
                    <Button
                        variant="outline"
                        w="40px"
                        minW="40px"
                        h="40px"
                        bg="white"
                        borderColor={checkboxMenuColors.popoverBorder}
                        borderRadius="8px"
                        p={0}
                        _hover={{ borderColor: checkboxMenuColors.brand }}
                    >
                        <Icon as={icon as ElementType} boxSize={4} color={checkboxMenuColors.icon} />
                    </Button>
                    </Box>
                ) : (
                    <Box w={width} ref={triggerRef}>
                        <SelectionSummaryTooltip
                            items={tokenLabels}
                            isDisabled={!isSubsetSelected || isPopoverOpen}
                            fillParent
                        >
                            <CheckboxMenuTrigger
                                isOpen={isPopoverOpen}
                                fillToken
                                tokens={triggerTokens}
                                emptyLabel={
                                    singleSelect
                                        ? 'Select location'
                                        : (locationIds === 'all' ? 'All Locations' : 'Select Locations')
                                }
                                onRemoveToken={
                                    (!singleSelect && !isSubsetSelected && locationIds !== 'all' && Array.isArray(locationIds))
                                        ? (index: number) => {
                                            const id = locationIds[index];
                                            if (id) handleRemoveLocation(id);
                                        }
                                        : undefined
                                }
                            />
                        </SelectionSummaryTooltip>
                    </Box>
                )}
            </PopoverTrigger>
            <PopoverContent
                w={typeof popoverWidth === 'number' ? `${popoverWidth}px` : popoverWidth}
                maxH="500px"
                display="flex"
                flexDirection="column"
                sx={checkboxMenuPopoverContentSx}
            >
                <PopoverBody p={0} display="flex" flexDirection="column" minH={0}>
                    {singleSelect ? (
                        <>
                            <CheckboxMenuSearch
                                value={searchTerm}
                                onChange={setSearchTerm}
                                placeholder="Search locations..."
                            />
                            <CheckboxMenuList>
                                {visibleLocations.map((location) => (
                                    <MenuRow
                                        key={location._id}
                                        kind="single"
                                        isSelected={selectedLocationId === location._id}
                                        onClick={() => handleSingleLocationSelect(location._id)}
                                    >
                                        {location.title}
                                    </MenuRow>
                                ))}
                            </CheckboxMenuList>
                        </>
                    ) : (
                    <>
                        <CheckboxMenuSearch
                            value={searchTerm}
                            onChange={setSearchTerm}
                            placeholder="Search locations..."
                        />
                        <CheckboxMenuPinnedAll
                            isSelected={locationIds === 'all'}
                            onClick={() => {
                                if (locationIds !== 'all') {
                                    handleAllLocationsChange({ target: { checked: true } } as ChangeEvent<HTMLInputElement>);
                                }
                            }}
                        >
                            All Locations
                        </CheckboxMenuPinnedAll>
                        <CheckboxMenuList>
                            {getMyLocations().length > 0 && (
                                <CheckboxMenuRow
                                    isSelected={
                                        Array.isArray(locationIds) &&
                                        getMyLocations().every(id => locationIds.includes(id))
                                    }
                                    onClick={() => {
                                        const checked =
                                            Array.isArray(locationIds) &&
                                            getMyLocations().every(id => locationIds.includes(id));
                                        handleMyLocationsChanges({ target: { checked: !checked } } as ChangeEvent<HTMLInputElement>);
                                    }}
                                >
                                    My Locations
                                </CheckboxMenuRow>
                            )}

                            {!hideOtherEmployeeLocations && getMyLocations().length > 0 && locationsData?.centralPartsEmployees && locationsData.centralPartsEmployees.length > 0 && (
                                <VStack align="stretch" spacing={2} px="4px" py="6px">
                                    <Divider borderColor={checkboxMenuColors.divider} />
                                    <Text fontSize="11px" fontWeight={600} color={checkboxMenuColors.muted} letterSpacing="0.07em">
                                        See Other's Location Groupings
                                    </Text>
                                    <Select
                                        size="sm"
                                        fontSize={checkboxMenuTriggerMetrics.fontSize}
                                        value={selectedEmployee}
                                        onChange={(e) => handleEmployeeLocationsChange(e.target.value)}
                                        bg="white"
                                        borderColor={checkboxMenuColors.popoverBorder}
                                    >
                                        <option value="none">None</option>
                                        {locationsData.centralPartsEmployees.map((employee) => (
                                            <option key={employee._id} value={employee._id}>
                                                {employee.name} ({employee.myLocations.length} locations)
                                            </option>
                                        ))}
                                    </Select>
                                    {selectedEmployee !== 'none' && (
                                        <HStack spacing={3} flexWrap="wrap">
                                            <HStack spacing={1}>
                                                <Box w={3} h={3} borderRadius="full" bg="blue.500" />
                                                <Text fontSize="xs" color={checkboxMenuColors.muted}>Yours</Text>
                                            </HStack>
                                            <HStack spacing={1}>
                                                <Box w={3} h={3} borderRadius="full" bg="purple.500" />
                                                <Text fontSize="xs" color={checkboxMenuColors.muted}>Theirs / Both</Text>
                                            </HStack>
                                        </HStack>
                                    )}
                                    {selectedEmployee !== 'none' && Array.isArray(locationIds) && locationIds.length > 0 && (() => {
                                        const employeeLocations = getSelectedEmployeeLocations();
                                        const myOriginalLocations = getMyLocations();
                                        return (
                                            <HStack spacing="5px" flexWrap="wrap">
                                                {locationIds.map(id => {
                                                    const loc = locationsData?.locations?.find(l => l._id === id);
                                                    if (!loc) return null;
                                                    const isInMyLocations = myOriginalLocations.includes(id);
                                                    const isInEmployeeLocations = employeeLocations.includes(id);
                                                    let color: string = checkboxMenuColors.brand;
                                                    if (isInMyLocations && isInEmployeeLocations) color = 'purple.500';
                                                    else if (isInEmployeeLocations && !isInMyLocations) color = 'orange.500';
                                                    return (
                                                        <TokenChip key={id} onRemove={() => handleRemoveLocation(id)}>
                                                            <Box as="span" color={color}>{loc.title}</Box>
                                                        </TokenChip>
                                                    );
                                                })}
                                            </HStack>
                                        );
                                    })()}
                                </VStack>
                            )}

                            {visibleLocations.map((location) => (
                                <CheckboxMenuRow
                                    key={location._id}
                                    isSelected={Array.isArray(locationIds) && locationIds.includes(location._id)}
                                    onClick={() => {
                                        const current = Array.isArray(locationIds) ? locationIds : [];
                                        const next = current.includes(location._id)
                                            ? current.filter(id => id !== location._id)
                                            : [...current, location._id];
                                        handleIndividualLocationsChange(next);
                                    }}
                                >
                                    {location.title}
                                </CheckboxMenuRow>
                            ))}
                        </CheckboxMenuList>
                        <CheckboxMenuFooter
                            selectedCount={selectedCount}
                            totalCount={totalLocations}
                            onClear={() => handleIndividualLocationsChange([])}
                        />
                    </>
                    )}
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};

export default LocationSelector;
